import React, { useEffect, useMemo, useRef, useState } from "react";
import {
  AppBar, Toolbar, Box, Paper, Typography, Chip, Tooltip, Drawer,
  List, ListItem, ListItemText, TextField, InputAdornment, IconButton,
  Divider, CircularProgress
} from "@mui/material";
import Autocomplete from "@mui/material/Autocomplete";
import SearchIcon from "@mui/icons-material/Search";
import TodayIcon from "@mui/icons-material/Today";
import CloseIcon from "@mui/icons-material/Close";
import CalendarMonthIcon from "@mui/icons-material/CalendarMonth";

/* -------------------------- Helpers -------------------------- */
const pad2 = (n) => String(n).padStart(2, "0");
const toISOFromDMY = (dmy) => {
  if (!dmy) return "";
  const [d, m, y] = dmy.split("/").map((x) => parseInt(x, 10));
  if (!d || !m || !y) return "";
  return `${y}-${pad2(m)}-${pad2(d)}`;
};
const toDMYFromISO = (iso) => {
  if (!iso) return "";
  const [y, m, d] = iso.split("-").map((x) => parseInt(x, 10));
  if (!d || !m || !y) return "";
  return `${pad2(d)}/${pad2(m)}/${y}`;
};
const normalizeTime = (t) => {
  if (!t) return t;
  const [h, m = "0"] = String(t).split(":");
  return `${pad2(parseInt(h, 10))}:${pad2(parseInt(m, 10))}`;
};
const timeToMinutes = (t) => {
  const [hh, mm] = t.split(":").map((x) => parseInt(x, 10));
  return hh * 60 + mm;
};
const buildMarks = (startMinutes, endMinutes, step = 30) => {
  const out = [];
  for (let m = startMinutes; m <= endMinutes; m += step) {
    const hh = Math.floor(m / 60), mm = m % 60;
    out.push(`${pad2(hh)}:${pad2(mm)}`);
  }
  return out;
};
const sumHours = (mins) => Math.round((mins / 60) * 100) / 100;
const clamp = (val, min, max) => Math.max(min, Math.min(max, val));

/* Fechas por defecto: hoy y hoy-7 */
const todayISO = () => {
  const d = new Date();
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};
const daysAgoISO = (n) => {
  const d = new Date();
  d.setDate(d.getDate() - n);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
};

/* -------------------------- Paleta / estilo -------------------------- */
const palette = {
  text: "#F7F9FF",
  subtext: "#E0E7F6",
  line: "rgba(255,255,255,0.22)",
  lineSoft: "rgba(255,255,255,0.12)",
  headerBg: "#0a0f1f",
  dayColBg: "#0b1220",
  lunch: "rgba(255,231,138,0.18)",
  pendiente: { bg: "rgba(255,210,77,0.22)", border: "#FFD24D" },
  en_proceso: { bg: "rgba(88,200,255,0.22)", border: "#8ED9FF" },
  completado: { bg: "rgba(110,255,186,0.20)", border: "#9CFFDA" },
};
const prioridadBorder = { alta: "#FF6B6B", media: "#FFB94D", baja: "#BFA7FF" };

/* Debounce helper */
function useDebounced(value, delay = 350) {
  const [v, setV] = React.useState(value);
  React.useEffect(() => {
    const id = setTimeout(() => setV(value), delay);
    return () => clearTimeout(id);
  }, [value, delay]);
  return v;
}

/* -------------------------- Componente principal -------------------------- */
export const AgendaGanttView = ({
  start = "08:30",
  end = "17:30",
  stepMin = 30,
  rowHeight = 92,
  pxPerStep = 128,
  dayColWidth = 170,            // más delgado
  defaultCodArea = 1,
  defaultCodUsuario = 61,
}) => {
  const startMin = timeToMinutes(start);
  const endMin = timeToMinutes(end);

  /* FILTROS */
  const [q, setQ] = useState("");
  const [usuario, setUsuario] = useState(defaultCodUsuario);
  const [usuariosOpts, setUsuariosOpts] = useState([]);
  const [loadingUsuarios, setLoadingUsuarios] = useState(false);

  // Default: hoy-7 → hoy
  const [dateFrom, setDateFrom] = useState(daysAgoISO(7));
  const [dateTo,   setDateTo]   = useState(todayISO());

  /* DATA */
  const [data, setData] = useState([]);
  const [loadingData, setLoadingData] = useState(false);
  const [errorData, setErrorData] = useState("");
  const [errorUsers, setErrorUsers] = useState("");

  /* DRAWER */
  const [drawer, setDrawer] = useState({ open: false, title: "", actividades: [] });

  /* REGLA DE TIEMPO */
  const marks = useMemo(() => buildMarks(startMin, endMin, stepMin), [startMin, endMin, stepMin]);
  const intervals = useMemo(() => {
    const arr = [];
    for (let i = 0; i < marks.length - 1; i++) arr.push({ from: marks[i], to: marks[i + 1], idx: i });
    return arr;
  }, [marks]);
  const colCount = intervals.length;

  /* -------------------------- Fetch: Usuarios -------------------------- */
  useEffect(() => {
    let alive = true;
    const loadUsers = async () => {
      setLoadingUsuarios(true);
      setErrorUsers("");
      try {
        const url = `https://xprojectwebapi.abexacloud.com/api/User?codArea=${encodeURIComponent(defaultCodArea)}`;
        const res = await fetch(url, { headers: { accept: "text/plain" } });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const json = await res.json();
        const content = Array.isArray(json?.content) ? json.content : [];
        const opts = content.map(u => ({ codUsuario: u.codUsuario, nomUsuario: u.nomUsuario }));
        if (alive) setUsuariosOpts(opts);
      } catch (e) {
        if (alive) setErrorUsers("No se pudo cargar usuarios.");
      } finally {
        if (alive) setLoadingUsuarios(false);
      }
    };
    loadUsers();
    return () => { alive = false; };
  }, [defaultCodArea]);

  /* -------------------------- Acción: PROCESAR -------------------------- */
  const lastFetchRef = useRef(0);
  const procesar = async () => {
    setLoadingData(true);
    setErrorData("");

    const nowSeq = Date.now();
    lastFetchRef.current = nowSeq;

    try {
      // Fechas con hora exigida: desde 00:00:00, hasta 23:59:59
      const fechaInicio = `${toDMYFromISO(dateFrom)} 00:00:00`;
      const fechaFin    = `${toDMYFromISO(dateTo)} 23:59:59`;
      const codUsuario  = usuario ?? defaultCodUsuario;

      const query = new URLSearchParams({
        fechaInicio,
        fechaFin,
        codUsuario: String(codUsuario),
      }).toString();

      const url = `https://tiemposcontroles.abexacloud.com/api/activities?${query}`;
      const res = await fetch(url, { headers: { accept: "application/json" } });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const json = await res.json();

      if (lastFetchRef.current !== nowSeq) return;

      const content = Array.isArray(json?.content) ? json.content : [];
      const normalized = content.map(d => ({
        ...d,
        diaISO: toISOFromDMY(d.dia),
        diaDate: (() => { try { return new Date(toISOFromDMY(d.dia)); } catch { return null; } })(),
        actividades: (d.actividades || []).map(a => ({
          ...a,
          horaInicio: normalizeTime(a.horaInicio),
          horaFin: normalizeTime(a.horaFin),
          prioridad: a.prioridad || "media",
          responsable: a.responsable || "—",
          estado: a.estado || "en_proceso",
          searchText: `${(a.nombreActividad||"")} ${(a.responsable||"")}`.toLowerCase(),
        })),
      }));
      setData(normalized);
    } catch (e) {
      setErrorData("No se pudo cargar actividades.");
    } finally {
      setLoadingData(false);
    }
  };

  /* -------------------------- Dataset filtrado -------------------------- */
  const debouncedQ = useDebounced(q, 350);
  const filtered = useMemo(() => {
    const df = dateFrom ? new Date(dateFrom) : null;
    const dt = dateTo ? new Date(dateTo) : null;
    const term = debouncedQ.trim().toLowerCase();

    return (data || [])
      .filter(d => {
        const di = d.diaDate || (d.diaISO ? new Date(d.diaISO) : null);
        return !df || !dt || !di ? true : di >= df && di <= dt;
      })
      .map(d => ({
        ...d,
        actividades: d.actividades.filter(a => {
          const txt = a.searchText || `${a.nombreActividad} ${a.responsable}`.toLowerCase();
          const okQ = !term || txt.includes(term);
          return okQ;
        })
      }))
      .filter(d => d.actividades.length > 0);
  }, [data, dateFrom, dateTo, debouncedQ]);

  /* -------------------------- KPI -------------------------- */
  const kpis = useMemo(() => {
    let actividades = 0;
    let minTotales = 0;
    const sMin = startMin, eMin = endMin;
    filtered.forEach((d) => {
      actividades += d.actividades.length;
      d.actividades.forEach((a) => {
        let ms = timeToMinutes(a.horaInicio);
        let me = timeToMinutes(a.horaFin);
        if (isNaN(ms) || isNaN(me)) return;
        if (me <= ms) me = ms + 30;
        ms = clamp(ms, sMin, eMin);
        me = clamp(me, sMin, eMin);
        minTotales += Math.max(0, me - ms);
      });
    });
    return { dias: filtered.length, actividades, horas: sumHours(minTotales) };
  }, [filtered, startMin, endMin]);

  /* -------------------------- Posicionamiento -------------------------- */
  const xFromTime = (t) => dayColWidth + ((timeToMinutes(t) - startMin) / stepMin) * pxPerStep;
  const wFromRange = (a) => {
    let ms = timeToMinutes(a.horaInicio);
    let me = timeToMinutes(a.horaFin);
    if (isNaN(ms) || isNaN(me)) return pxPerStep * 0.6;
    if (me <= ms) me = ms + 30;
    ms = clamp(ms, startMin, endMin);
    me = clamp(me, startMin, endMin);
    const steps = (me - ms) / stepMin;
    return Math.max(pxPerStep * 0.55, steps * pxPerStep);
  };
  const isLunch = (from) => {
    const m = timeToMinutes(from);
    return m >= 13 * 60 && m < 14 * 60;
  };

  /* -------------------------- Drawer helpers -------------------------- */
  const abrirDrawerIntervalo = (d, it) => {
    const s = timeToMinutes(it.from), e = timeToMinutes(it.to);
    const acts = d.actividades
      .filter((a) => {
        let aS = timeToMinutes(a.horaInicio), aE = timeToMinutes(a.horaFin);
        if (aE <= aS) aE = aS + 30;
        return Math.max(aS, s) < Math.min(aE, e);
      })
      .map((x) => ({ ...x, __day: d.dia }));
    setDrawer({ open: true, title: `${d.dia} · ${it.from}–${it.to}`, actividades: acts });
  };
  const abrirDrawerActividad = (d, a) =>
    setDrawer({ open: true, title: `${d.dia} · ${a.horaInicio}–${a.horaFin}`, actividades: [{ ...a, __day: d.dia }] });

  const setUltimos7 = () => {
    setDateFrom(daysAgoISO(7));
    setDateTo(todayISO());
  };

  /* -------------------------- UI -------------------------- */
  return (
    <Box sx={{ bgcolor: "#080c18", color: palette.text, minHeight: "100vh" }}>
      {/* ==== FILTROS STICKY ==== */}
      <AppBar
        position="sticky"
        elevation={0}
        sx={{
          bgcolor: palette.headerBg,
          borderBottom: `1px solid ${palette.lineSoft}`,
          zIndex: 10
        }}
      >
        <Toolbar sx={{ gap: 1.25, flexWrap: "wrap" }}>
          {/* Desde / Hasta (más delgados) */}
          <TextField
            label="Desde"
            type="date"
            size="small"
            value={dateFrom}
            onChange={(e) => setDateFrom(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              width: 170,
              borderRadius: 1.2,
              "& .MuiInputBase-root": { height: 38 },
              input: { color: '#ffffff', py: 0.75 },
              label: { color: '#ffffff' },
            }}
            InputProps={{ startAdornment: <InputAdornment position="start"><TodayIcon sx={{ color: '#ffffff' }} /></InputAdornment> }}
          />
          <TextField
            label="Hasta"
            type="date"
            size="small"
            value={dateTo}
            onChange={(e) => setDateTo(e.target.value)}
            InputLabelProps={{ shrink: true }}
            sx={{
              width: 170,
              borderRadius: 1.2,
              "& .MuiInputBase-root": { height: 38 },
              input: { color: '#ffffff', py: 0.75 },
              label: { color: '#ffffff' },
            }}
            InputProps={{ startAdornment: <InputAdornment position="start"><TodayIcon sx={{ color: '#ffffff' }} /></InputAdornment> }}
          />

          {/* Usuario */}
          <Autocomplete
            sx={{ width: 260 }}
            size="small"
            loading={loadingUsuarios}
            options={usuariosOpts}
            getOptionLabel={(o) => o?.nomUsuario ?? ""}
            value={usuariosOpts.find(o => o.codUsuario === usuario) || null}
            onChange={(_, val) => setUsuario(val?.codUsuario ?? null)}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Usuario"
                InputLabelProps={{ shrink: true }}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {loadingUsuarios ? <CircularProgress color="inherit" size={16} sx={{ mr: 1 }} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
                sx={{
                  caretColor: '#ffffff',
                  borderRadius: 1.2,
                  input: { color: '#ffffff' },
                  label: { color: '#ffffff' },
                }}
              />
            )}
            isOptionEqualToValue={(opt, val) => opt.codUsuario === val.codUsuario}
          />

          {/* Procesar */}
          <Tooltip title="Procesar">
            <span>
              <button
                onClick={procesar}
                disabled={loadingData}
                style={{
                  backgroundColor: loadingData ? '#2b5fa1' : '#336fbb',
                  padding: '6px 12px',
                  borderRadius: '8px',
                  border: '1px solid rgba(255,255,255,0.2)',
                  color: '#fff',
                  fontWeight: 700,
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: '8px',
                  cursor: loadingData ? 'not-allowed' : 'pointer'
                }}
              >
                {loadingData && <CircularProgress size={16} color="inherit" />}
                <span>Procesar</span>
              </button>
            </span>
          </Tooltip>

          {/* Buscador */}
          <TextField
            size="small"
            placeholder="Buscar actividad o responsable"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            variant="outlined"
            sx={{
              width: 360,
              borderRadius: 1.2,
              input: { color: '#ffffff' },
              caretColor: '#ffffff',
            }}
            InputProps={{ startAdornment: (<InputAdornment position="start"><SearchIcon sx={{ color: '#9ec3ff' }} /></InputAdornment>) }}
          />

          {/* Título a la derecha */}
          <Box sx={{ ml: "auto", display: "flex", alignItems: "center", gap: 1 }}>
            <CalendarMonthIcon sx={{ color: palette.text }} />
            <Typography variant="h6" sx={{ fontWeight: 800, whiteSpace: "nowrap" }}>
              Reporte Gantt
            </Typography>
          </Box>
        </Toolbar>
      </AppBar>

      {/* KPIs justo debajo de filtros, encima del gráfico */}
      <Box sx={{ mx: 2, mt: 1.25, mb: 0.5, display: "flex", gap: 1, alignItems: "center" }}>
        <Chip size="small" label={`Días: ${filtered.length}`} sx={{ bgcolor: "transparent", color: palette.text, border: `1px solid ${palette.lineSoft}` }} />
        <Chip size="small" label={`Actividades: ${filtered.reduce((a,d)=>a+d.actividades.length,0)}`} sx={{ bgcolor: "transparent", color: palette.text, border: `1px solid ${palette.lineSoft}` }} />
        <Chip size="small" label={`Horas: ${kpis.horas}`} sx={{ bgcolor: "transparent", color: palette.text, border: `1px solid ${palette.lineSoft}` }} />
        <Box sx={{ ml: "auto" }}>
          {(errorUsers || errorData) && (
            <Typography variant="caption" sx={{ color: "#ffd7d7" }}>
              {errorUsers ? `⚠ ${errorUsers}` : ""}{errorUsers && errorData ? " · " : ""}{errorData ? `⚠ ${errorData}` : ""}
            </Typography>
          )}
        </Box>
      </Box>

      {/* Contenedor principal */}
      <Paper elevation={0} sx={{ m: 2, mt: 0, p: 0, borderRadius: 10, bgcolor: "transparent", border: `1px solid ${palette.lineSoft}` }}>
        <Box
          sx={{
            position: "relative",
            maxHeight: "74vh",
            overflow: "auto",
            WebkitOverflowScrolling: "touch",
            scrollbarWidth: "none",
            msOverflowStyle: "none",
            "&::-webkit-scrollbar": { display: "none" },
          }}
        >
          {/* Header sticky del grid */}
          <Box
            sx={{
              position: "sticky",
              top: 0,
              zIndex: 3,
              display: "grid",
              gridTemplateColumns: `${dayColWidth}px repeat(${colCount}, ${pxPerStep}px)`,
              borderBottom: `1px solid ${palette.lineSoft}`,
              bgcolor: palette.headerBg
            }}
          >
            <HeaderCell
              sx={{
                position: "sticky",
                left: 0,
                zIndex: 4,
                color: "#fff",
                bgcolor: palette.dayColBg,
                borderRight: `1px solid ${palette.lineSoft}`,
                textAlign: "center",
                justifyContent: "center"
              }}
            >
              Día
            </HeaderCell>

            {intervals.map((it, i) => (
              <HeaderCell key={i} sx={{ p: 0, position: "relative", bgcolor: isLunch(it.from) ? palette.lunch : "transparent" }}>
                <Box sx={{ px: 1, py: 0.5, display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                  <Typography
                    variant={it.from.endsWith(":00") ? "body2" : "caption"}
                    sx={{ color: palette.text, opacity: it.from.endsWith(":00") ? 1 : 0.75, fontWeight: 800 }}
                  >
                    {it.from}
                  </Typography>
                  {i === intervals.length - 1 && (
                    <Typography variant="caption" sx={{ color: palette.text, opacity: 0.95, fontWeight: 800 }}>
                      {it.to}
                    </Typography>
                  )}
                </Box>
                <Box sx={{ position: "absolute", right: 0, top: 0, bottom: 0, width: 1, bgcolor: palette.line }} />
              </HeaderCell>
            ))}
          </Box>

          {/* Body */}
          {filtered.map((d, rowIdx) => (
            <Box
              key={rowIdx}
              sx={{
                display: "grid",
                gridTemplateColumns: `${dayColWidth}px repeat(${colCount}, ${pxPerStep}px)`,
                minHeight: rowHeight,
                alignItems: "stretch",
                borderBottom: `1px solid ${palette.lineSoft}`,
                position: "relative",
              }}
            >
              {/* Columna día sticky — SOLO UNA FECHA centrada y con énfasis */}
              <BodyStickyCell
                sx={{
                  left: 0,
                  bgcolor: palette.dayColBg,
                  borderRight: `1px solid ${palette.lineSoft}`,
                  alignItems: "center",
                }}
              >
                <Typography variant="body2" sx={{ fontWeight: 900, color: palette.text, letterSpacing: .3, textAlign: "center" }}>
                  {d.dia /* ej: 13/10/2025 */}
                </Typography>
              </BodyStickyCell>

              {/* Fondo intervalos clicable */}
              {intervals.map((it, i) => (
                <Box
                  key={i}
                  onClick={() => abrirDrawerIntervalo(d, it)}
                  sx={{
                    borderRight: `1px dashed ${palette.lineSoft}`,
                    bgcolor: isLunch(it.from) ? palette.lunch : "transparent",
                    cursor: "pointer",
                    "&:hover": { bgcolor: isLunch(it.from) ? "rgba(255,231,138,0.22)" : "rgba(255,255,255,0.06)" },
                    transition: "background-color .15s ease",
                  }}
                />
              ))}

              {/* Tarjetas actividades: menos redondeadas, sin icono, texto siempre visible, hora debajo */}
              {d.actividades.map((a, i) => {
                const left = xFromTime(a.horaInicio);
                const width = wFromRange(a);
                const visual = palette[a.estado] || palette.en_proceso;

                return (
                  <Box
                    key={i}
                    onClick={(e) => { e.stopPropagation(); abrirDrawerActividad(d, a); }}
                    sx={{
                      position: "absolute",
                      left,
                      top: 10,
                      width,
                      minWidth: 80,
                      height: rowHeight - 20,
                      borderRadius: 6,                    // menos redondeado
                      background: visual.bg,
                      border: `1px solid ${prioridadBorder[a.prioridad] || visual.border}`,
                      display: "flex",
                      flexDirection: "column",
                      justifyContent: "center",
                      gap: 0.25,
                      px: 10,
                      py: 0.75,
                      overflow: "hidden",
                      cursor: "pointer",
                      "&:hover": { outline: `2px solid ${visual.border}` },
                      transition: "outline-color .12s ease",
                    }}
                    title={`${a.nombreActividad} · ${a.horaInicio}–${a.horaFin}`}
                  >
                    {/* Texto: que siempre se vea algo */}
                    <Typography
                      variant="subtitle2"
                      sx={{
                        fontWeight: 900,
                        color: palette.text,
                        letterSpacing: .2,
                        display: "-webkit-box",
                        WebkitLineClamp: 2,        // hasta 2 líneas
                        WebkitBoxOrient: "vertical",
                        overflow: "hidden"
                      }}
                    >
                      {a.nombreActividad}
                    </Typography>

                    {/* Horario debajo del texto */}
                    <Box
                      sx={{
                        mt: 0.25,
                        alignSelf: "flex-start",
                        display: "inline-flex",
                        alignItems: "center",
                        justifyContent: "center",
                        px: 6,
                        py: 0.25,
                        borderRadius: 6,
                        border: `1px solid ${visual.border}`,
                        bgcolor: "rgba(255,255,255,0.06)",
                      }}
                    >
                      <Typography variant="caption" sx={{ color: palette.text, fontWeight: 800, lineHeight: 1 }}>
                        {a.horaInicio}–{a.horaFin}
                      </Typography>
                    </Box>
                  </Box>
                );
              })}
            </Box>
          ))}

          {/* Leyenda */}
          <Box sx={{ display: "flex", alignItems: "center", gap: 2, p: 1.5 }}>
            <Legend color={palette.pendiente.bg} text="Amarillos" />
            <Legend color={palette.en_proceso.bg} text="Celestes" />
            <Legend color={palette.completado.bg} text="Verdes" />
            <Legend color={palette.lunch} text="Refrigerio 13:00–14:00" border />
          </Box>
        </Box>
      </Paper>

      {/* Drawer detalle — tareas como chips */}
      <Drawer anchor="right" open={drawer.open} onClose={() => setDrawer({ ...drawer, open: false })}>
        <Box sx={{ width: 500, bgcolor: "#0c1224", color: palette.text, height: "100%" }}>
          <Box sx={{ p: 2, display: "flex", alignItems: "center", justifyContent: "space-between", borderBottom: `1px solid ${palette.lineSoft}` }}>
            <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{drawer.title || "Detalle"}</Typography>
            <IconButton onClick={() => setDrawer({ ...drawer, open: false })} sx={{ color: palette.text }}>
              <CloseIcon />
            </IconButton>
          </Box>

          {drawer.actividades?.length ? (
            <Box sx={{ p: 2 }}>
              <List dense>
                {drawer.actividades.map((a, idx) => (
                  <React.Fragment key={idx}>
                    <ListItem disableGutters alignItems="flex-start">
                      <ListItemText
                        primaryTypographyProps={{ sx: { color: palette.text, fontWeight: 700 } }}
                        secondaryTypographyProps={{ sx: { color: palette.subtext } }}
                        primary={`${a.__day || ""}${a.__day ? " · " : ""}${a.nombreActividad} (${a.horaInicio}–${a.horaFin})`}
                        secondary={`Responsable: ${a.responsable}`}
                      />
                    </ListItem>

                    {a.tareas?.length ? (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, px: 2, pb: 1 }}>
                        {a.tareas.map((t, i) => (
                          <Chip
                            key={i}
                            label={t}
                            size="small"
                            sx={{
                              bgcolor: "rgba(255,255,255,0.10)",
                              color: palette.text,
                              border: `1px solid ${palette.lineSoft}`
                            }}
                          />
                        ))}
                      </Box>
                    ) : null}

                    {idx < drawer.actividades.length - 1 && <Divider sx={{ my: 1, borderColor: palette.lineSoft }} />}
                  </React.Fragment>
                ))}
              </List>
            </Box>
          ) : (
            <Box sx={{ p: 3, color: palette.subtext }}>No hay actividades en ese intervalo.</Box>
          )}
        </Box>
      </Drawer>
    </Box>
  );
};

/* -------------------------- Subcomponentes -------------------------- */
function HeaderCell({ children, sx = {} }) {
  return (
    <Box
      sx={{
        p: 1,
        fontWeight: 800,
        color: "#FFFFFF",
        display: "flex",
        alignItems: "center",
        position: "relative",
        userSelect: "none",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}
function BodyStickyCell({ children, sx = {} }) {
  return (
    <Box
      sx={{
        position: "sticky",
        zIndex: 2,
        px: 1,
        py: 1,
        display: "flex",
        flexDirection: "column",
        justifyContent: "center",
        userSelect: "none",
        ...sx,
      }}
    >
      {children}
    </Box>
  );
}
function Legend({ color, text, border = false }) {
  return (
    <Box sx={{ display: "inline-flex", alignItems: "center", gap: 1 }}>
      <Box
        sx={{
          width: 14, height: 14, borderRadius: 3,
          bgcolor: color,
          border: border ? "1px solid rgba(255,255,255,0.25)" : "1px solid rgba(255,255,255,0.12)",
        }}
      />
      <Typography variant="caption" sx={{ color: "#FFF" }}>{text}</Typography>
    </Box>
  );
}

export default AgendaGanttView;
